import { memo } from 'react'
import { FarmBarn, FarmWindmill } from './FarmDrawing'

const PALETTES = [
  {
    sky: '#a9dce2',
    horizon: '#fff3ce',
    hill: '#88b7ac',
    grass: '#adc78b',
    leaf: '#609778',
    light: '#98bb83',
    water: '#93c9c2',
  },
  {
    sky: '#94c8dc',
    horizon: '#fbe8b9',
    hill: '#80aaa5',
    grass: '#aac085',
    leaf: '#538a70',
    light: '#91b27d',
    water: '#82bab9',
  },
  {
    sky: '#b8b2d6',
    horizon: '#ffd3b1',
    hill: '#9aa6ad',
    grass: '#b3ba88',
    leaf: '#6b8e79',
    light: '#b5bb84',
    water: '#a6bbc5',
  },
  {
    sky: '#647ca8',
    horizon: '#e8bca9',
    hill: '#7f94a7',
    grass: '#98a887',
    leaf: '#526e72',
    light: '#8a9e86',
    water: '#849eb7',
  },
]
const TREES = [
  { x: 65, kind: 'oak', scale: 1 },
  { x: 320, kind: 'pine', scale: 0.88 },
  { x: 590, kind: 'birch', scale: 1.06 },
  { x: 865, kind: 'willow', scale: 0.88 },
  { x: 1130, kind: 'blossom', scale: 0.92 },
  { x: 1450, kind: 'fruit', scale: 1 },
  { x: 1690, kind: 'pine', scale: 1.12 },
]

/** Decorative layers only. The existing game clock freezes every detail on pause. */
export const FrisbeePark = memo(function FrisbeePark({
  id,
  at,
  distance,
  stage,
  reducedMotion,
  jungle = false,
  farm = false,
}: {
  id: string
  at: number
  distance: number
  stage: number
  reducedMotion: boolean
  jungle?: boolean
  farm?: boolean
}) {
  const p = jungle
    ? {
        ...PALETTES[stage],
        leaf: '#35634c',
        light: '#719258',
        grass: '#7d9c65',
        hill: '#668970',
      }
    : PALETTES[stage]
  const travel = reducedMotion ? 0 : distance
  const clock = reducedMotion ? 0 : at
  const paint = (name: string) => `url(#${id}-park-${name})`
  const offset = (travel * 0.3) % 1800
  return (
    <g
      aria-hidden="true"
      pointerEvents="none"
      data-frisbee-habitat={jungle ? 'jungle' : farm ? 'farm' : 'park'}
    >
      <defs>
        <linearGradient id={`${id}-park-sky`} x2="0" y2="1">
          <stop stopColor={p.sky} />
          <stop offset="1" stopColor={p.horizon} />
        </linearGradient>
        <linearGradient id={`${id}-park-grass`} x2="0" y2="1">
          <stop stopColor={p.leaf} />
          <stop offset="1" stopColor={p.grass} />
        </linearGradient>
        <linearGradient id={`${id}-park-trail`} x2="0" y2="1">
          <stop stopColor="#f3e4bf" />
          <stop offset="1" stopColor="#ddc69c" />
        </linearGradient>
        <radialGradient id={`${id}-park-glow`}>
          <stop stopColor="#fff4c8" stopOpacity=".5" />
          <stop offset="1" stopColor="#fff4c8" stopOpacity="0" />
        </radialGradient>
        <pattern
          id={`${id}-park-grain`}
          width="110"
          height="42"
          patternUnits="userSpaceOnUse"
          patternTransform={`translate(${-distance % 110} 0)`}
        >
          <path
            d="M9 11h9m34 18h15m24-22h6M27 36h4"
            stroke="#b5996e"
            strokeWidth="1.3"
            strokeLinecap="round"
            opacity=".45"
          />
          <g fill="#fff3d4" opacity=".8">
            <ellipse cx="24" cy="20" rx="2.5" ry="1" />
            <ellipse cx="83" cy="35" rx="3" ry="1" />
          </g>
          <g fill="#ad9269" opacity=".3">
            <circle cx="40" cy="6" r=".8" />
            <circle cx="76" cy="17" r="1" />
            <circle cx="6" cy="32" r=".8" />
          </g>
        </pattern>
        <g id={`${id}-park-oak`}>
          <ellipse cy="3" rx="47" ry="6" fill="#355b56" opacity=".15" />
          <path d="M-5 0-4-88h9L5 0Z" fill="#866f52" />
          <path d="M0-48-23-30M0-66l24-20" stroke="#78644e" strokeWidth="4" fill="none" />
          <path
            d="M-45-61q-30-32 3-47-4-31 29-32 22-26 44-4 33-5 39 24 33 13 17 43-7 25-47 19-28 17-46-3Z"
            fill={p.leaf}
          />
          <path
            d="M-43-103q1-20 25-17 19-24 39-10 24-5 33 17"
            fill="none"
            stroke={p.light}
            strokeWidth="13"
            strokeLinecap="round"
            opacity=".6"
          />
          <path
            d="M-26-86h9m23-26h10m18 32h10m-40-47h5"
            stroke="#d7e1ab"
            strokeWidth="3"
            strokeLinecap="round"
            opacity=".4"
          />
          <path
            d="M-16 0q1-12 5-8m5 8 6-10 3 10m8-2 3-7"
            fill="none"
            stroke={p.leaf}
            strokeWidth="2"
          />
        </g>
        <g id={`${id}-park-pine`}>
          <ellipse cy="2" rx="35" ry="5" fill="#355b56" opacity=".13" />
          <path d="M-4 0v-114h8V0" fill="#8b735a" />
          <path
            d="m0-149-26 44h12l-25 37h15l-27 41h102L24-68h15l-25-37h12Z"
            fill={p.leaf}
          />
          <path
            d="m0-141-15 31h15m0 12-24 26H0m0 13-34 26H0"
            fill={p.light}
            opacity=".65"
          />
          <path
            d="m8-98 11 20m-4 20 12 16"
            stroke="#294f55"
            strokeWidth="3"
            opacity=".2"
          />
        </g>
        <g id={`${id}-park-birch`}>
          <ellipse cy="2" rx="30" ry="5" fill="#355b56" opacity=".13" />
          <path
            d="M-16-86q-23-10-12-37-12-35 16-41 25-23 43 4 30 10 13 44 11 30-21 34Z"
            fill={p.light}
          />
          <path
            d="M-20-130q-1-21 20-26"
            stroke="#dbe4b0"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            opacity=".6"
          />
          <path d="M-3 0 0-137 7-139 4 0Z" fill="#eee8ce" />
          <path
            d="M2-34h4M1-64h4m-4-33h5M3-14H0m3-70 13-18m-14-8-12-14"
            stroke="#827f69"
            strokeWidth="2"
          />
        </g>
        <g id={`${id}-park-willow`}>
          <ellipse cy="3" rx="48" ry="6" fill="#355b56" opacity=".16" />
          <path d="M-7 0q18-45 4-99h10Q21-40 2 0Z" fill="#7e7555" />
          <path
            d="M-60-52q-3-89 60-89 63-2 62 87-18-30-37-33Q0-55-18-91q-27 6-42 39Z"
            fill={p.leaf}
          />
          <path
            d="M-39-102q-12 21-11 49m30-66q-11 23-12 52m32-58q-2 30-13 53m28-50q20 18 24 58m-7-37q17 23 20 47"
            fill="none"
            stroke={p.light}
            strokeWidth="5"
            strokeLinecap="round"
            opacity=".65"
          />
        </g>
        <g id={`${id}-park-blossom`}>
          <ellipse cy="3" rx="37" ry="5" fill="#355b56" opacity=".13" />
          <path
            d="M0 0v-85m0 30-23-29m23 20 22-28"
            stroke="#887162"
            strokeWidth="7"
            fill="none"
          />
          <path
            d="M-34-62q-29-17-13-42-1-24 27-27 18-26 39-6 37-6 36 26 23 29-8 46-20 16-39 1-19 13-42 2Z"
            fill="#d7a7a1"
          />
          <path
            d="M-37-100q2-17 24-17 19-24 37-7"
            fill="none"
            stroke="#f3cec1"
            strokeWidth="12"
            strokeLinecap="round"
          />
          <g fill="#fff0d8" opacity=".8">
            <circle cx="-26" cy="-84" r="3" />
            <circle cx="22" cy="-94" r="3" />
            <circle cx="3" cy="-119" r="2" />
            <circle cx="-4" cy="-75" r="2.5" />
          </g>
        </g>
        <g id={`${id}-park-fruit`}>
          <use href={`#${id}-park-oak`} />
          <g fill="#d99b61" stroke="#b1794e" strokeWidth="1">
            <circle cx="-27" cy="-92" r="5" />
            <circle cx="26" cy="-80" r="5" />
            <circle cx="12" cy="-120" r="4" />
          </g>
          <path d="m-27-98 3-4m10-24 3-3m10 44 3-4" stroke="#476d56" strokeWidth="2" />
        </g>
        <g id={`${id}-park-bench`}>
          <ellipse cy="1" rx="40" ry="4" fill="#375953" opacity=".12" />
          <path
            d="M-25-18v18m50-18v18m-50-27v14m50-14v14"
            stroke="#526b66"
            strokeWidth="3"
          />
          <path
            d="M-30-29h60m-60 7h60m-65 10h70"
            stroke="#b58e63"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <path d="M-31-31h62m-64 17h66" stroke="#ead0a0" strokeWidth="1" />
        </g>
      </defs>
      <path fill={paint('sky')} d="M0 0h640v320H0Z" />
      <circle cx="508" cy={70 + stage * 15} r="100" fill={paint('glow')} />
      <circle cx="508" cy={70 + stage * 15} r="26" fill="#fff5cd" opacity=".9" />
      <path
        d="m472 0-150 257h46L508 0Zm62 0L455 259h25L560 0Z"
        fill="#fff4cf"
        opacity=".045"
      />
      <g transform={`translate(${(-travel * 0.035) % 900} 0)`} fill="#fffcf0">
        {[0, 900].map((x) => (
          <g key={x} transform={`translate(${x} 0)`}>
            <path
              d="M32 65q-5-14 13-18 4-27 29-23 23-9 35 17 23-1 23 21Z"
              opacity=".64"
            />
            <path d="M301 39q2-12 19-10 17-23 34-4 23-5 28 14Z" opacity=".46" />
            <path
              d="M628 91q-3-13 15-18 10-31 32-16 26-8 34 20 15-1 19 14Z"
              opacity=".5"
            />
            <path d="M46 66h70m530 25h67" stroke={p.sky} strokeWidth="4" opacity=".16" />
          </g>
        ))}
      </g>
      <g transform={`translate(${(-travel * 0.075) % 1000} 0)`}>
        {[0, 1000].map((x) => (
          <g key={x} transform={`translate(${x} 0)`}>
            <path
              d="M0 196 107 122 190 179 324 107 441 181 576 135 702 188 833 115 1000 191V256H0Z"
              fill={p.hill}
              opacity=".36"
            />
            <path
              d="M0 217Q114 130 245 192T530 185 791 180 1000 199V262H0Z"
              fill={p.hill}
              opacity=".75"
            />
            <path
              d="M0 217q53-54 111-6 30-52 71-17 49-62 99 12 44-29 74-8 45-47 82 2 42-40 92 6 50-45 96-8 43-60 87 4 65-40 104 3 45-57 94-4 39-36 90 1v57H0Z"
              fill={p.leaf}
              opacity=".32"
            />
          </g>
        ))}
      </g>
      <path d="M0 239q160-19 320 0t320 0v81H0Z" fill={paint('grass')} />
      <g transform={`translate(${(-travel * 0.18) % 1300} 0)`}>
        {[0, 1300].map((x) => (
          <g key={x} transform={`translate(${x} 0)`}>
            <ellipse cx="440" cy="237" rx="115" ry="11" fill="#e7dfb8" opacity=".65" />
            <ellipse cx="440" cy="234" rx="109" ry="10" fill={p.water} />
            <path
              d={`M365 232h${34 + Math.sin(clock / 900) * 6}m31-4h47m-26 10h38m-75-1h20`}
              stroke="#eef0d2"
              strokeWidth="1.5"
              opacity=".55"
              strokeLinecap="round"
            />
            <path
              d="M338 235v-15m-4 13-3-10m155 22v-12m6 11 3-9"
              stroke="#628366"
              strokeWidth="2"
            />
            <g transform="translate(690 244)" opacity=".6">
              <path
                d="M0 0v-25m20 25v-29m20 29v-26m20 26v-24m20 24v-25m20 25v-25"
                stroke="#eee1b9"
                strokeWidth="4"
              />
              <path d="M-3-18h108M-3-7h108" stroke="#e4d8b2" strokeWidth="3" />
            </g>
            <g transform="translate(1040 229)" opacity=".65">
              <path d="M-30 0v-47m60 47v-47" stroke="#e7d9b4" strokeWidth="5" />
              <path d="M-42-47 0-73 42-47Z" fill="#708f88" />
              <path d="M-34-45h68" stroke="#f3dfb6" strokeWidth="4" />
              <path d="M-30-24h60" stroke="#d2c9a2" strokeWidth="2" />
            </g>
          </g>
        ))}
      </g>
      {[0, 1800].flatMap((tile) =>
        TREES.map((tree, index) => {
          const x = tile + tree.x - offset
          if (x < -100 || x > 750) return null
          return (
            <g
              key={`${tile}-${index}`}
              transform={`translate(${x} 246) scale(${tree.scale})`}
              opacity=".88"
            >
              <use
                href={`#${id}-park-${jungle ? (index % 2 ? 'willow' : 'oak') : tree.kind}`}
              />
            </g>
          )
        })
      )}
      {farm && !jungle && (
        <g>
          {[-900, 0, 900].map((tile) => (
            <g key={tile} transform={`translate(${tile - ((travel * 0.16) % 900)} 0)`}>
              <g transform="translate(300 123) scale(.65)">
                <FarmBarn />
              </g>
              <g transform="translate(565 115) scale(.6)">
                <FarmWindmill rotation={clock / 70} />
              </g>
            </g>
          ))}
        </g>
      )}
      {!jungle && !farm && (
        <g transform={`translate(${-offset} 0)`}>
          {[0, 1800].map((tile) => (
            <g key={tile} transform={`translate(${tile} 0)`}>
              <use href={`#${id}-park-bench`} transform="translate(210 245) scale(.85)" />
              <use href={`#${id}-park-bench`} transform="translate(1270 245) scale(.8)" />
              <g transform="translate(730 244)">
                <path d="M0 0v-65" stroke="#617776" strokeWidth="3" />
                <path
                  d="M-8-66h16l-3 13H-5Z"
                  fill={stage > 1 ? '#f4d68e' : '#e5e6cc'}
                  stroke="#617776"
                  strokeWidth="2"
                />
                <path d="M-10-68 0-75 10-68" fill="#617776" />
                {stage > 1 && <circle cy="-60" r="20" fill={paint('glow')} />}
              </g>
            </g>
          ))}
        </g>
      )}
      {jungle && (
        <g fill={p.leaf}>
          <path d="M0 0h640v18q-55 34-105 4-55 41-105 3-54 40-110 2-61 39-113 0-62 38-106-1Q30 71 0 39Z" />
          <g
            fill="none"
            stroke={p.light}
            strokeWidth="3"
            transform={`translate(${Math.sin(clock / 2200) * 2} 0)`}
          >
            <path d="M64 0q-13 46 9 72m175-72q23 29 2 72m345-72q-25 52-7 85" />
          </g>
          <g transform={`translate(${300 - (offset % 800)} 238)`}>
            <path d="M-35 0h83l-6-17-65-4Z" fill="#776449" />
            <path d="m-19-12 43 4" stroke="#ad9867" strokeWidth="2" />
            <path d="m-6-17-10-17q21-7 25 17m8 0q5-24 28-16L28-15Z" fill={p.light} />
          </g>
        </g>
      )}
      {/* Birds stay high above the pickup and obstacle lanes. */}
      {[0, 1, 2].map((index) => {
        const x = 720 - ((clock * 0.033 + index * 245) % 860)
        const y = 47 + index * 16 + Math.sin(clock / 1300 + index) * 7
        const wing = Math.sin(clock / 150 + index) * 5
        return (
          <g
            key={index}
            transform={`translate(${x} ${y}) scale(${0.8 + index * 0.12})`}
            fill="none"
            stroke="#567584"
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity=".72"
          >
            <path
              d={`M-11 ${-3 - wing}Q-4 ${-5 - wing} 0 1q5 ${-6 - wing} 11 ${-4 - wing}`}
            />
            <path d="m-1 1 3 1" strokeWidth="3" />
          </g>
        )
      })}
      <g transform={`translate(${(-travel * 0.65) % 320} 0)`}>
        {[0, 320, 640, 960].map((x) => (
          <g key={x} transform={`translate(${x} 0)`}>
            <path
              d="M20 260q-5-8-9-8m9 8 3-13m-3 13 9-7m155 7-4-9m4 9 8-12"
              stroke="#608365"
              strokeWidth="1.5"
              fill="none"
            />
            <path
              d="M82 258v-13m-4 7 4 2 4-4m159 9v-12"
              stroke="#628463"
              strokeWidth="1.5"
              fill="none"
            />
            <g fill="#fff0c1">
              <circle cx="82" cy="245" r="3.5" />
              <circle cx="245" cy="248" r="3" />
            </g>
            <circle cx="82" cy="245" r="1.2" fill="#d3a24b" />
            <ellipse cx="136" cy="257" rx="9" ry="2" fill="#c3d19a" opacity=".5" />
          </g>
        ))}
      </g>
      <path d="M0 268h640v52H0Z" fill={paint('trail')} />
      <path d="M0 267h640" stroke="#f7eccf" strokeWidth="5" />
      <path d="M0 273h640" stroke="#bcac82" strokeWidth="1" opacity=".35" />
      <path d="M0 280h640v40H0Z" fill={paint('grain')} />
      <path
        d="M0 291q170-4 320 1t320-1m-640 19q230 4 640-2"
        fill="none"
        stroke="#fff0ce"
        strokeWidth="1"
        opacity=".32"
      />
      <g transform={`translate(${-travel % 460} 0)`}>
        {[0, 460, 920].map((x) => (
          <g key={x} transform={`translate(${x} 0)`} opacity=".65">
            <path
              d="M12 320q-4-9-11-10m11 10 6-13m-6 13 18-6m297 6-9-7m9 7 8-11"
              stroke="#8eaa73"
              strokeWidth="2"
              fill="none"
            />
            <ellipse cx="211" cy="311" rx="4" ry="1.5" fill="#bca27b" />
            <ellipse cx="218" cy="313" rx="2" ry="1" fill="#ad9776" />
          </g>
        ))}
      </g>
    </g>
  )
})
